import React from 'react';
import { IndianRupee, ShoppingBag } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { DEPT_NAMES } from '@/lib/constants';
import { STATUS_COLORS } from '@/lib/status-colors';
import {
  ActionRow, Card, ChipRow, Pager, PhonePage, StatusPill, TopBar,
} from '@/components/phone/primitives';
import { SelectField } from '@/components/phone/FieldControls';
import PaymentSheet from '@/components/phone/PaymentSheet';
import { Colors, statusTone } from '@/components/phone/theme';

/**
 * One customer, on a phone.
 *
 * The desktop profile is a fact band, an aging strip, the order ledger and a
 * payments rail beside it. Stacked at a handset's width those are one long
 * column of cards: who they are, what the account stands at, every order with
 * its balance, then the money received. Recording a payment opens as a sheet
 * at the thumb rather than a pane off the right edge.
 */

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const date = (d) => formatIST(d, { dateOnly: true });

const ORDER_STATUSES = ['design', 'in_production', 'ready_for_dispatch', 'dispatched', 'delivered'];
const PAYMENT_LABEL = { unpaid: 'Unpaid', partially_paid: 'Part paid', fully_paid: 'Paid' };
const PAYMENT_TONE = { unpaid: 'critical', partially_paid: 'caution', fully_paid: 'positive' };

function Fact({ label, value, tone }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.5px]" style={{ color: Colors.text.tertiary }}>{label}</p>
      <p className="truncate text-[13px] font-medium" style={{ color: tone || Colors.text.primary }}>{value || '—'}</p>
    </div>
  );
}

function OrderCard({ order: o, canSeeFinancials, canRecordPayments, onOpen, onPay }) {
  const balance = (o.order_value || 0) - (o.total_paid || 0);
  const items = o.line_items || [];
  return (
    <li
      data-testid={`customer-order-${o.id}`}
      className="mt-3 rounded-[10px] border"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button type="button" onClick={onOpen} className="block w-full px-4 pt-3 text-left">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[14px] font-bold" style={{ color: Colors.text.primary }}>{o.order_id}</span>
          <span className="shrink-0 text-[11px]" style={{ color: Colors.text.tertiary }}>{date(o.created_at)}</span>
        </span>
        <span className="mt-0.5 block truncate text-[12px]" style={{ color: Colors.text.secondary }}>
          {items.length
            ? items.slice(0, 3).map((it) => `${DEPT_NAMES[it.order_type] || it.order_type}${it.quantity ? ` ×${it.quantity}` : ''}`).join(', ')
              + (items.length > 3 ? ` +${items.length - 3}` : '')
            : 'No items'}
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-2">
          <StatusPill label={STATUS_COLORS[o.status]?.label || o.status} tone={statusTone(o.status)} dot />
          {canSeeFinancials && (
            <StatusPill label={PAYMENT_LABEL[o.payment_status] || 'Unpaid'} tone={PAYMENT_TONE[o.payment_status] || 'critical'} />
          )}
        </span>
        {canSeeFinancials && (
          <span className="flex items-center justify-between gap-2 py-3 text-[12px] tabular-nums">
            <span style={{ color: Colors.text.secondary }}>
              {money(o.order_value)} · paid {money(o.total_paid)}
            </span>
            <span className="font-semibold" style={{ color: balance > 0 ? Colors.status.error : Colors.status.success }}>
              {balance > 0 ? `${money(balance)} due` : 'Settled'}
            </span>
          </span>
        )}
        {!canSeeFinancials && <span className="block py-3" />}
      </button>
      {canRecordPayments && balance > 0 && (
        <div className="border-t px-3 py-2" style={{ borderColor: Colors.border.subtle }}>
          <button
            type="button"
            data-testid={`pay-order-${o.id}`}
            onClick={onPay}
            className="flex h-9 items-center gap-1 rounded-md border px-3 text-[12px] font-semibold"
            style={{ backgroundColor: Colors.background.primary, borderColor: '#D2E2D8', color: Colors.semantic.positive }}
          >
            <IndianRupee className="h-3.5 w-3.5" /> Record payment
          </button>
        </div>
      )}
    </li>
  );
}

export default function PhoneCustomerProfile({
  profile, canSeeFinancials, canRecordPayments,
  orders, ordersTotal, ordersPage, ordersPages, onOrdersPage,
  statusFilter, onStatus, paymentStatusFilter, onPaymentStatus,
  payments, paymentsTotal, paymentsPage, paymentsPages, onPaymentsPage,
  payment,
  onBack, onRecordPayment, onNewOrder, onOpenOrder,
}) {
  const { customer, financial_summary: fin, outstanding_orders, aging_summary, customer_since, total_orders } = profile;
  const aging = aging_summary || {};
  const outstanding = fin?.outstanding || 0;

  return (
    <PhonePage testID="customer-profile-page">
      <TopBar eyebrow="CUSTOMER" title={customer.customer_name} subtitle={customer.firm_name} onBack={onBack} />

      <div className="flex-1 px-4 pb-10">
        <ActionRow
          testID="customer-actions"
          actions={[
            canRecordPayments && {
              key: 'pay', label: 'Record payment', icon: IndianRupee, primary: true,
              onClick: () => onRecordPayment(), testID: 'record-payment-action',
            },
            { key: 'order', label: 'New order', icon: ShoppingBag, tone: 'accent', onClick: onNewOrder, testID: 'new-order-action' },
          ]}
        />

        <Card title="Contact" testID="customer-facts">
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            <Fact label="Mobile" value={customer.mobile_number} />
            <Fact label="Email" value={customer.email} />
            <Fact label="GSTIN" value={customer.gstin} />
            <Fact label="Since" value={customer_since ? date(customer_since) : null} />
            <Fact label="Orders" value={String(total_orders || 0)} />
            <Fact label="Open orders" value={String(outstanding_orders?.length || 0)} />
          </div>
          {(customer.billing_address || customer.delivery_address) && (
            <div className="mt-3 space-y-2 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
              {customer.billing_address && (
                <p className="text-[12px]" style={{ color: Colors.text.secondary }}>
                  <span className="font-semibold" style={{ color: Colors.text.tertiary }}>Billing · </span>{customer.billing_address}
                </p>
              )}
              {customer.delivery_address && (
                <p className="text-[12px]" style={{ color: Colors.text.secondary }}>
                  <span className="font-semibold" style={{ color: Colors.text.tertiary }}>Delivery · </span>{customer.delivery_address}
                </p>
              )}
            </div>
          )}
        </Card>

        {canSeeFinancials && (
          <Card className="mt-3" title="Account" testID="customer-account">
            <div className="grid grid-cols-2 gap-3">
              <Fact label="Billed" value={money(fin?.total_value)} />
              <Fact label="Paid" value={money(fin?.total_paid)} tone={Colors.status.success} />
              <Fact label="Outstanding" value={money(outstanding)} tone={outstanding > 0 ? Colors.status.error : Colors.status.success} />
              <Fact label="Advance held" value={money(fin?.advance_balance)} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
              <Fact label="0–30 days" value={money(aging['0_30'] ?? aging.days_0_30)} />
              <Fact label="31–60" value={money(aging['31_60'] ?? aging.days_31_60)} tone={Colors.status.warning} />
              <Fact label="60+" value={money(aging['60_plus'] ?? aging.days_60_plus)} tone={Colors.status.error} />
            </div>
          </Card>
        )}

        <div className="mt-5 flex items-baseline justify-between px-1">
          <h2 className="text-[15px] font-bold" style={{ color: Colors.text.primary }}>Orders</h2>
          <span className="text-[12px]" style={{ color: Colors.text.tertiary }}>{orders.length} of {ordersTotal}</span>
        </div>
        {canSeeFinancials && (
          <div className="mt-2 space-y-2">
            <ChipRow
              chips={[
                { key: 'all', label: `All ${ordersTotal}` },
                { key: 'unpaid', label: `Unpaid ${aging.unpaid_count ?? '—'}` },
              ]}
              active={paymentStatusFilter === 'all' ? 'all' : paymentStatusFilter}
              onSelect={onPaymentStatus}
              testIDPrefix="payment-chip"
            />
            <SelectField
              name="order_status" label="Order status" value={statusFilter}
              options={[
                { value: 'all', label: 'Any status' },
                ...ORDER_STATUSES.map((s) => ({ value: s, label: STATUS_COLORS[s]?.label || s })),
              ]}
              onChange={onStatus}
            />
          </div>
        )}
        {orders.length === 0 ? (
          <p className="mt-3 px-1 text-[13px]" style={{ color: Colors.text.tertiary }}>No orders for this customer yet</p>
        ) : (
          <ul data-testid="customer-orders-list">
            {orders.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                canSeeFinancials={canSeeFinancials}
                canRecordPayments={canRecordPayments}
                onOpen={() => onOpenOrder(o)}
                onPay={() => onRecordPayment(o.id)}
              />
            ))}
          </ul>
        )}
        <Pager page={ordersPage} totalPages={ordersPages} onPage={onOrdersPage} testID="orders-pager" />

        {canSeeFinancials && (
          <Card className="mt-5" title="Payments received" aside={`${paymentsTotal}`} testID="customer-payments">
            {payments.length === 0 ? (
              <p className="py-2 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>No payments recorded</p>
            ) : payments.map((p, i) => (
              <div
                key={p.id || i}
                className="flex items-start justify-between gap-2 border-b py-2.5 last:border-b-0"
                style={{ borderColor: Colors.border.subtle }}
              >
                <span className="min-w-0">
                  <span className="block truncate text-[13px] font-medium" style={{ color: Colors.text.primary }}>
                    {p.order_display_id || p.order_id || 'On account'}
                  </span>
                  <span className="block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
                    {p.mode}{p.reference_number ? ` · ${p.reference_number}` : ''}
                    {p.date ? ` · ${formatIST(p.date, { compact: true })}` : ''}
                    {p.recorded_by_name ? ` · ${p.recorded_by_name}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-[13px] font-bold tabular-nums" style={{ color: Colors.status.success }}>
                  {money(p.amount)}
                </span>
              </div>
            ))}
            <Pager page={paymentsPage} totalPages={paymentsPages} onPage={onPaymentsPage} testID="payments-pager" />
          </Card>
        )}
      </div>

      {canRecordPayments && <PaymentSheet payment={payment} />}
    </PhonePage>
  );
}
